import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { Store } from '@ngxs/store';
import { ClientSelectionState } from '../../../../state/ClientSelectionState/ClientSelection.state';
import { PayrollService } from '../../../../services/payroll.service';
import {
  PayDateInfoEx,
  PayrollStartData,
  PayTypes,
  TimeclockModel
} from '../../../../models/payroll';
import { Router } from '@angular/router';
import { AuthService } from '../../../../services/auth.service';
import { forkJoin } from 'rxjs';
import { ApiResponse } from '../../../../models/api-response';
import { WarningLevel } from '../../../../models/error-detail';
import { SharedService } from '../../../../services/shared.service';

@Component({
  selector: 'lib-timeclock-check-v2',
  standalone: false,
  templateUrl: './timeclock-check-v2.component.html',
  styleUrl: './timeclock-check-v2.component.scss'
})
export class TimeclockCheckV2Component implements OnInit {
  @Input()
  public selCalendarId = null;
  @Output()
  public editTimeclockFile = new EventEmitter<{ file: TimeclockModel; pdi: PayDateInfoEx }>();

  public selTimeclockFile: TimeclockModel = null;
  public chosenTimeclockFile: TimeclockModel = null;

  public uploadedFile: File;
  public isCsv: boolean | null = null;
  public meetsFileSize: boolean | null = null;

  public maxFileSize: number = 30;

  public hasValidTimeclock: boolean;
  public isCsr: boolean;
  public loaded: boolean;
  public pdis: PayDateInfoEx[];
  public selTimeclockId: number | null;
  public timeclockFiles: TimeclockModel[];
  public fileRemoved = false;
  public errorMessage: string | null = null;
  private startData: PayrollStartData;
  private clientCode: string;
  private deletedFileId: number | null = null;

  constructor(
    private store: Store,
    private payrollService: PayrollService,
    private router: Router,
    private authSerivce: AuthService,
    private sharedService: SharedService
  ) {}

  ngOnInit() {
    this.store.selectOnce(ClientSelectionState.clientCode).subscribe(code => {
      this.clientCode = code;
      this.payrollService.getPayrollStartData(code).subscribe(startData => {
        this.startData = startData;
        if (
          this.startData.ActivePayroll &&
          this.startData.ActivePayroll.PayType != PayTypes.Manual
        ) {
          //Active, nonmanual payroll. Needs to be redirected.
          if (this.authSerivce.isCurrentUser(this.startData.ActivePayroll.LastEditBy)) {
            this.router.navigate([`/client/${code}/payroll`]);
          } else {
            this._goToPayrollStart();
          }
        } else {
          this._getTimeclockFiles();
        }
      });
    });
  }

  public undeleteFile() {
    this.payrollService
      .undeleteTimeclockFile(this.clientCode, this.deletedFileId)
      .subscribe(result => {
        this.selTimeclockFile = result;
        this.selTimeclockId = result.Id;
        this.deletedFileId = null;
      });
  }

  public deleteFile() {
    this.payrollService
      .deleteTimeclockFile(
        this.clientCode,
        this.selTimeclockFile.Id,
        this.selTimeclockFile.PersistedToAgile
      )
      .subscribe(result => {
        this.deletedFileId = this.selTimeclockFile.Id;
        this.selTimeclockFile = null;
        this.selTimeclockId = null;
        this.fileRemoved = true;
      });
  }

  public uploadFile(file: any) {
    this.errorMessage = null;
    this.uploadedFile = file.target.files[0];
    const periodEndString: any = this.pdis[0].PeriodEnd;
    const periodEnd = new Date(periodEndString);
    const month = periodEnd.getMonth() + 1;
    const monthString = month < 10 ? `0${month}` : `${month}`;
    const date = periodEnd.getDate() < 10 ? `0${periodEnd.getDate()}` : `${periodEnd.getDate()}`;
    const periodEndFormattedString = `${periodEnd.getFullYear()}${monthString}${date}`;
    this.isCsv = this.uploadedFile.type == 'text/csv';
    this.meetsFileSize = this.uploadedFile.size / (1024 * 1024) <= this.maxFileSize;
    if (this.isCsv && this.meetsFileSize) {
      this.payrollService
        .uploadTimeclockFile(this.clientCode, this.uploadedFile, periodEndFormattedString)
        .subscribe(
          result => {
            this.editTimeclockFile.emit({ file: result.Header, pdi: this.pdis[0] });
          },
          err => {
            this.errorMessage = err.Message;
          }
        );
    }
  }

  private _goToPayrollStart(params?: {}) {
    // if calendar_id query param exists, append it back to the query params
    // to the payroll start page
    if (this.selCalendarId) {
      params = { ...params, ...{ calendar_id: this.selCalendarId } };
    }

    this.router.navigate([`/client/${this.clientCode}/payroll`], { queryParams: params });
  }

  private _getPayDateInfos(): PayDateInfoEx[] {
    // FIXME: single client location assumption
    const location = this.startData.Locations[0];

    let payDates: PayDateInfoEx[] = [];

    if (this.selCalendarId) {
      // if calendar_id query param exists,
      // only search for timeclock files matching that period end
      payDates.push(location.NextPayDates.find(x => x.CalendarId == this.selCalendarId));
    } else {
      payDates = location.NextPayDates;
    }

    return payDates;
  }

  private _getTimeclockFiles() {
    // query agile api for timeclock files for each period end date
    // do request in parallel
    const pdis = this._getPayDateInfos();
    this.pdis = pdis;
    const reqs = [];
    for (let pdi of pdis) {
      reqs.push(this.payrollService.findTimeclockFile(this.clientCode, pdi.PeriodEnd));
    }

    forkJoin(reqs).subscribe((files: ApiResponse<TimeclockModel>[]) => {
      this.timeclockFiles = [];
      for (let file of files) {
        // if the file model and messages are both null - there is no file.
        if (file == null || (file.Messages == null && file.Model == null)) {
          continue;
        }

        // timeclock file is valid if there are no messages,
        // or messages does not contain any critical errors
        const valid =
          file.Messages == null ||
          !file.Messages.find(x => x.WarningLevel == WarningLevel.Critical);

        if (file.Model != null || file.Messages != null) {
          const tc = new TimeclockModel(
            file.Model?.Id,
            file.Model?.PeriodEndDate,
            valid,
            file.Messages
          );
          tc.FileName = file.Model?.FileName;
          tc.PersistedToAgile = file.Model?.PersistedToAgile;
          this.timeclockFiles.push(tc);
        }
      }
      if (this.timeclockFiles.length == 1 && this.timeclockFiles[0].Id) {
        this.selTimeclockId = this.timeclockFiles[0].Id;
        this.selTimeclockFile = this.timeclockFiles[0];
      } else if (
        this.timeclockFiles.length == 1 &&
        this.timeclockFiles[0].Messages != null &&
        this.timeclockFiles[0].Messages.length > 0
      ) {
        this.selTimeclockFile = this.timeclockFiles[0];
      } else {
        this.selTimeclockId = null;
      }

      // see if there are and valid timeclock files with no critical errors
      this.hasValidTimeclock =
        this.timeclockFiles.find(
          x => !x.Messages?.find(m => m.WarningLevel == WarningLevel.Critical)
        ) != null;

      this.loaded = true;
    });
  }

  public runPayroll(useFile: boolean = false) {
    if (this.selTimeclockFile == null && this.chosenTimeclockFile != null) {
      this.selTimeclockFile = this.chosenTimeclockFile;
    }
    this.sharedService.timeclockSelection = {};
    if (useFile) {
      this.editTimeclockFile.emit({ file: this.selTimeclockFile, pdi: this.pdis[0] });
    } else {
      this._goToPayrollStart();
    }
  }

  protected readonly WarningLevel = WarningLevel;
}
